import { Reservation, ReservationTicketType } from '@models/reservation.model';
import { TicketType } from '@models/ticket-type.model';
import { BehaviorSubject, lastValueFrom, Observable, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiResponse, ApiService } from '@services/api.service';
import { OrderService } from '@services/order.service';
import { Order } from '@models/order.model';
import { EventBusService } from './event-bus.service';
import { AppEvent } from '@models/events/app-event.enum';
import { EventData } from '@models/events/event-data.model';
import { ReservationTimer } from '@models/reservation-timer.interface';
import { User } from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class ReservationService {
  private reservationCache = new BehaviorSubject<Reservation | null>(null);
  private reservationTimerCache = new BehaviorSubject<ReservationTimer | null>(
    null,
  );
  loadedReservation: boolean = false;

  constructor(
    private apiService: ApiService,
    private eventBusService: EventBusService,
    private orderService: OrderService,
  ) {}

  get reservation() {
    return this.reservationCache.asObservable();
  }

  get reservationSnapshot() {
    return this.reservationCache.value;
  }

  get reservationTimer() {
    return this.reservationTimerCache.asObservable();
  }

  getActiveReservation() {
    return this.apiService
      .get<ApiResponse<User>>('me', {
        load: [
          'activeReservations.items.ticketType.performances',
          'activeReservations.event',
          'activeReservations.promoCode',
          'activeReservations.items.preDiscountTicketType',
        ],
      })
      .pipe(
        tap({
          next: (response: any) => {
            this.loadedReservation = true;
            if (response.data.active_reservations.length) {
              this.setReservation(response.data.active_reservations[0]);
            }
          },
          error: () => {
            this.loadedReservation = true;
          },
        }),
      );
  }

  setReservation(reservation: Reservation | null) {
    this.reservationCache.next(reservation);

    if (reservation) {
      this.updateTimer();
    }
  }

  addOns(reservationId: number): Observable<ApiResponse<TicketType[]>> {
    return this.apiService.get<ApiResponse<TicketType[]>>(
      `reservations/${reservationId}/add-ons`,
    );
  }

  createOrUpdate(
    eventId: number,
    ticketTypes: any[] = [],
  ): Observable<ApiResponse<Reservation>> {
    const params = {
      event_id: eventId,
      ticket_types: ticketTypes.filter((tt: any) => tt.quantity > 0),
      load: ['items.ticketType.performances', 'event'],
    };

    let currentReservation = this.reservationSnapshot;

    if (currentReservation) {
      if (currentReservation.event_id !== eventId) {
        try {
          lastValueFrom(this.cancel());
        } catch {}

        currentReservation = null;
      } else {
        this.eventBusService.emit(
          new EventData(AppEvent.UpdateCart, currentReservation),
        );
      }
    }

    return currentReservation
      ? this.update(currentReservation.id, params)
      : this.store(params);
  }

  private store(params: any): Observable<ApiResponse<Reservation>> {
    params.type = 'regular';

    return this.apiService
      .post<ApiResponse<Reservation>>('reservations', params)
      .pipe(
        tap({
          next: (response: ApiResponse<Reservation>) => {
            this.setReservation(response.data);
            this.eventBusService.emit(
              new EventData(AppEvent.AddToCart, this.reservationSnapshot),
            );
          },
        }),
      );
  }

  update(id: number, params: any): Observable<ApiResponse<Reservation>> {
    return this.apiService
      .patch<ApiResponse<Reservation>>(`reservations/${id}`, params)
      .pipe(
        tap({
          next: (response: ApiResponse<Reservation>) => {
            this.setReservation(response.data);
            this.eventBusService.emit(
              new EventData(AppEvent.AddToCart, this.reservationSnapshot),
            );
          },
        }),
      );
  }

  updateRefundGuaranteeOptedIn(
    optedIn: boolean,
  ): Observable<ApiResponse<Reservation>> {
    const reservation = this.reservationSnapshot;

    if (reservation == null) {
      throw new Error('Expected a reservationSnapshot but it is null');
    }

    return this.update(reservation.id, {
      refund_guarantee_opted_in: optedIn,
      load: [
        'items.ticketType.performances',
        'items.preDiscountTicketType',
        'event',
        'promoCode',
      ],
    });
  }

  createPaymentIntent(params: any): Observable<ApiResponse<any>> {
    if (this.reservationSnapshot == null) {
      throw new Error('Expected a reservationSnapshot but it is null');
    }
    return this.apiService.post<ApiResponse<any>>(
      `reservations/${this.reservationSnapshot.id}/payment-intent`,
      params,
    );
  }

  applyPromoCode(code: string): Observable<ApiResponse<Reservation>> {
    const reservation = this.reservationSnapshot;

    if (reservation == null) {
      throw new Error('Expected a reservationSnapshot but it is null');
    }

    return this.apiService
      .post<ApiResponse<Reservation>>(
        `reservations/${reservation.id}/apply-promo-code`,
        {
          code: code.toUpperCase(),
          load: [
            'items.ticketType.performances',
            'items.preDiscountTicketType',
            'event',
            'promoCode',
          ],
        },
      )
      .pipe(
        tap({
          next: (response: ApiResponse<Reservation>) => {
            const reservation = this.reservationSnapshot;
            if (reservation) {
              reservation.promo_code = response.meta.promo_code;
              this.setReservation(reservation);
            }
          },
        }),
      );
  }

  complete(params: any = {}): Observable<ApiResponse<any>> {
    const reservation = this.reservationSnapshot;

    if (reservation == null) {
      throw new Error('Expected a reservationSnapshot but it is null');
    }

    // Reserved seating

    return this.apiService
      .post<
        ApiResponse<any>
      >(`reservations/${reservation.id}/complete`, params)
      .pipe(
        tap({
          next: (response: ApiResponse<any>) => {
            if (response.data.id) {
              this.setReservation(null);
              this.orderService.addOrder(response.data);
            }
          },
        }),
      );
  }

  cancel(): Observable<ApiResponse<null>> {
    if (this.reservationSnapshot == null) {
      throw new Error('Expected a reservationSnapshot but it is null');
    }
    return this.apiService
      .post<
        ApiResponse<null>
      >(`reservations/${this.reservationSnapshot.id}/cancel`, {})
      .pipe(
        tap({
          next: (response: ApiResponse<null>) => {
            this.setReservation(null);
            sessionStorage.removeItem('selectedSeats');
          },
        }),
      );
  }

  private updateTimer() {
    const reservation = this.reservationSnapshot;

    if (reservation && this.reservationSnapshot) {
      const now = new Date();
      const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000); // 15 minutes
      const endDate = new Date(
        this.reservationSnapshot.expire_at.replace(/ /g, 'T'),
      );
      let seconds = Math.floor((endDate.getTime() - utc.getTime()) / 1000);
      const minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      if (minutes > 0 || seconds > 0) {
        this.reservationTimerCache.next({ minutes, seconds });

        setTimeout(() => {
          this.updateTimer();
        }, 1000);
        return;
      }

      this.eventBusService.emit(
        new EventData(AppEvent.ReservationTimedOut, this.reservationSnapshot),
      );
    }

    this.reservationCache.next(null);
    this.reservationTimerCache.next(null);
  }
}
